import { useQuery } from '@tanstack/react-query'
import {
  fetchMyPnlEventOptions,
  fetchMyPnlGrandTotal,
  fetchMyPnlList,
  fetchMyPnlMarkets,
  fetchMyPnlUsers,
  type MyPnlEventRow,
  type MyPnlFilter,
} from '@/api/map/myPnl'
import { keys } from '@/api/hooks/shared/keys'

export function useMyPnl(filter: MyPnlFilter & { run: number }) {
  return useQuery({
    queryKey: keys.myPnl({
      from: filter.from,
      to: filter.to,
      subGame: filter.subGame,
      eventId: filter.eventId ?? 'All',
      typeName: filter.typeName ?? 'All',
      page: filter.page ?? 1,
      run: filter.run,
    }),
    queryFn: () =>
      fetchMyPnlList({
        from: filter.from,
        to: filter.to,
        subGame: filter.subGame,
        eventId: filter.eventId,
        typeName: filter.typeName,
        page: filter.page,
      }),
    placeholderData: (prev) => prev,
  })
}

export function useMyPnlGrandTotal(filter: MyPnlFilter & { run: number }) {
  return useQuery({
    queryKey: keys.myPnlGrandTotal({
      from: filter.from,
      to: filter.to,
      subGame: filter.subGame,
      eventId: filter.eventId ?? 'All',
      typeName: filter.typeName ?? 'All',
      run: filter.run,
    }),
    queryFn: () =>
      fetchMyPnlGrandTotal({
        from: filter.from,
        to: filter.to,
        subGame: filter.subGame,
        eventId: filter.eventId,
        typeName: filter.typeName,
      }),
    placeholderData: (prev) => prev,
  })
}

/** Event Name picker — caller disables for Card Game / Live Casino like Lab. */
export function useMyPnlEvents(
  filter: {
    from: string
    to: string
    subGame: string
    run: number
  },
  enabled = true,
) {
  return useQuery({
    queryKey: keys.myPnlEvents({
      from: filter.from,
      to: filter.to,
      subGame: filter.subGame,
      run: filter.run,
    }),
    queryFn: () =>
      fetchMyPnlEventOptions({
        from: filter.from,
        to: filter.to,
        subGame: filter.subGame,
      }),
    enabled: enabled && filter.run > 0,
    staleTime: 30_000,
  })
}

export function useMyPnlMarkets(
  filter: MyPnlFilter & { run: number },
  row: Pick<MyPnlEventRow, 'eventId' | 'eventName' | 'subGame'> | null,
  enabled: boolean,
) {
  return useQuery({
    queryKey: keys.myPnlMarkets({
      from: filter.from,
      to: filter.to,
      subGame: row?.subGame ?? filter.subGame,
      eventId: row?.eventId ?? '',
      eventName: row?.eventName ?? '',
      typeName: filter.typeName ?? 'All',
      run: filter.run,
    }),
    queryFn: () => fetchMyPnlMarkets(filter, row!),
    enabled: enabled && row != null,
  })
}

export function useMyPnlUsers(
  filter: MyPnlFilter & { run: number },
  row: Pick<MyPnlEventRow, 'eventId' | 'eventName' | 'subGame'> | null,
  enabled: boolean,
) {
  return useQuery({
    queryKey: keys.myPnlUsers({
      from: filter.from,
      to: filter.to,
      subGame: row?.subGame ?? filter.subGame,
      eventId: row?.eventId ?? '',
      eventName: row?.eventName ?? '',
      typeName: filter.typeName ?? 'All',
      run: filter.run,
    }),
    queryFn: () => fetchMyPnlUsers(filter, row!),
    enabled: enabled && row != null,
  })
}
